import React, { useState, useEffect } from 'react';
import { FaBook, FaVideo, FaLink, FaFile, FaPlus, FaSearch, FaFolder } from 'react-icons/fa';
import axios from 'axios';
import './ResourceLibrary.css';
import config from '../../config';

const API_BASE_URL = `${config.API_BASE_URL}/api/study-tools`;

const ResourceLibrary = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newResource, setNewResource] = useState({
    title: '',
    description: '',
    url: '',
    type: 'link',
    category: ''
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/resources`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setResources(response.data);
      
      // Extract unique categories from resources
      const uniqueCategories = [...new Set(response.data.map(resource => resource.category))].filter(Boolean);
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError('Failed to load resources. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddResource = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/resources`, newResource, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Add the new resource with the returned ID
      setResources([...resources, { 
        ...newResource, 
        id: response.data.id,
        createdAt: new Date().toISOString()
      }]);
      
      // If this is a new category, add it to our list
      if (newResource.category && !categories.includes(newResource.category)) {
        setCategories([...categories, newResource.category]);
      }
      
      // Reset the form
      setNewResource({
        title: '',
        description: '',
        url: '',
        type: 'link',
        category: ''
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error creating resource:', error);
      alert('Failed to add resource. Please try again.');
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'document':
        return <FaFile />;
      case 'video':
        return <FaVideo />;
      case 'book':
        return <FaBook />;
      default:
        return <FaLink />;
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="resource-library">
        <div className="loading-spinner">Loading resources...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resource-library">
        <div className="error-message">
          {error}
          <button onClick={fetchResources} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="resource-library">
      <div className="resource-header">
        <h2>Resource Library</h2>
        <button 
          className="add-resource-btn"
          onClick={() => setShowAddForm(true)}
        >
          <FaPlus /> Add Resource
        </button>
      </div>

      <div className="resource-filters">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="category-filters">
          <button
            className={selectedCategory === 'all' ? 'active' : ''}
            onClick={() => setSelectedCategory('all')}
          >
            All
          </button>
          {categories.map(category => (
            <button
              key={category}
              className={selectedCategory === category ? 'active' : ''}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {showAddForm && (
        <div className="add-resource-form">
          <h3>Add New Resource</h3>
          <form onSubmit={handleAddResource}>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={newResource.title}
                onChange={(e) => setNewResource({...newResource, title: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newResource.description}
                onChange={(e) => setNewResource({...newResource, description: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>URL</label>
              <input
                type="url"
                value={newResource.url}
                onChange={(e) => setNewResource({...newResource, url: e.target.value})}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <select
                  value={newResource.type}
                  onChange={(e) => setNewResource({...newResource, type: e.target.value})}
                >
                  <option value="link">Link</option>
                  <option value="document">Document</option>
                  <option value="video">Video</option>
                  <option value="book">Book</option>
                </select>
              </div>

              <div className="form-group">
                <label>Category</label>
                <input
                  type="text"
                  value={newResource.category}
                  onChange={(e) => setNewResource({...newResource, category: e.target.value})}
                  list="categories"
                  required
                />
                <datalist id="categories">
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </datalist>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">Add Resource</button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="resources-grid">
        {filteredResources.length > 0 ? (
          filteredResources.map(resource => (
            <div key={resource.id} className="resource-card">
              <div className="resource-icon">
                {getResourceIcon(resource.type)}
              </div>
              <div className="resource-content">
                <h3>{resource.title}</h3>
                <p>{resource.description}</p>
                <div className="resource-meta">
                  <span className="category">
                    <FaFolder /> {resource.category}
                  </span>
                  <span className="date">
                    Added: {new Date(resource.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <a 
                href={resource.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="resource-link"
              >
                Open Resource
              </a>
            </div>
          ))
        ) : (
          <div className="no-resources">
            <p>No resources found. Try adjusting your filters or add new resources.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceLibrary; 